// src/pages/QrScans.js
import React, { useEffect, useState } from 'react';
import { getAllScans } from '../services/api';

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function QrScans() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab]  = useState('vehicle'); // vehicle | family
  const [filter,  setFilter]  = useState('all');
  const [search,  setSearch]  = useState('');

  const load = () => {
    setLoading(true);
    getAllScans()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const vehicleScans = data?.recentVehicleScans || [];
  const familyScans  = data?.recentFamilyScans  || [];

  const filteredVehicle = vehicleScans.filter(s => {
    const matchFilter = filter === 'all' ? true : filter === 'alert' ? s.alertSent : !s.alertSent;
    const matchSearch = !search.trim() ||
      (s.scannerName    || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.scannerPhone   || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.scannerMessage || '').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const filteredFamily = familyScans.filter(s => {
    const matchFilter = filter === 'all' ? true : filter === 'alert' ? s.emergencyAlert : !s.emergencyAlert;
    const matchSearch = !search.trim() ||
      (s.scannerName    || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.scannerPhone   || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.scannerMessage || '').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const tabStyle = (tab) => ({
    padding: '8px 20px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
    borderBottom: activeTab === tab ? '2px solid #0066cc' : '2px solid transparent',
    color: activeTab === tab ? '#0066cc' : '#666',
    background: 'transparent',
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>QR Scans</h1>
          <p>All QR code scans across the platform</p>
        </div>
        <button className="btn btn-secondary" onClick={load}>🔄 Refresh</button>
      </div>

      {/* Summary stats — uniform vehicle + family */}
      {data && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-icon blue">📱</div>
            <div className="stat-info">
              <h3>{data.totalScans ?? '—'}</h3>
              <p>Total QR Scans</p>
              <p style={{ fontSize: 11, color: '#888' }}>Vehicle + Family</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange">🚗</div>
            <div className="stat-info">
              <h3>{data.totalVehicleScans ?? '—'}</h3>
              <p>Vehicle QR Scans</p>
              <p style={{ fontSize: 11, color: '#888' }}>
                {data.totalVehicleAlerts ?? 0} with alert sent
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green">👨‍👩‍👧</div>
            <div className="stat-info">
              <h3>{data.totalFamilyScans ?? '—'}</h3>
              <p>Family QR Scans</p>
              <p style={{ fontSize: 11, color: '#888' }}>
                {data.totalFamilyEmergency ?? 0} emergency alerts
              </p>
            </div>
          </div>
          <div className="stat-card" style={{ background: '#fff5f5', border: '1px solid #f5c6cb' }}>
            <div className="stat-icon red">🚨</div>
            <div className="stat-info">
              <h3>{(data.totalVehicleAlerts ?? 0) + (data.totalFamilyEmergency ?? 0)}</h3>
              <p>Total Alerts Sent</p>
              <p style={{ fontSize: 11, color: '#888' }}>Vehicle + Family emergencies</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {/* Tabs */}
        <div style={{ borderBottom: '1px solid #dee2e6', marginBottom: 16, display: 'flex', gap: 0 }}>
          <button style={tabStyle('vehicle')} onClick={() => { setActiveTab('vehicle'); setFilter('all'); setSearch(''); }}>
            🚗 Vehicle Scans ({vehicleScans.length})
          </button>
          <button style={tabStyle('family')} onClick={() => { setActiveTab('family'); setFilter('all'); setSearch(''); }}>
            👨‍👩‍👧 Family Scans ({familyScans.length})
          </button>
        </div>

        {/* Filters */}
        <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Scans</option>
            <option value="alert">{activeTab === 'vehicle' ? 'Alert Sent' : 'Emergency Only'}</option>
            <option value="view">{activeTab === 'vehicle' ? 'Silent Views' : 'Info Views'}</option>
          </select>
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, phone, message..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 280 }}
          />
          <span style={{ color: '#666', fontSize: 13 }}>
            {activeTab === 'vehicle' ? filteredVehicle.length : filteredFamily.length} scans
          </span>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"/>Loading...</div>
        ) : activeTab === 'vehicle' ? (
          /* Vehicle scans table */
          filteredVehicle.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🚗</div><p>No vehicle scans found</p></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date/Time</th>
                    <th>Type</th>
                    <th>Finder Name</th>
                    <th>Finder Phone</th>
                    <th>Message</th>
                    <th>Location</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicle.map((scan, idx) => (
                    <tr key={scan.id}>
                      <td style={{ color: '#888' }}>{idx + 1}</td>
                      <td style={{ fontSize: 12 }}>{fmt(scan.createdAt)}</td>
                      <td>
                        {scan.alertSent
                          ? <span className="badge badge-danger">🚨 Alert Sent</span>
                          : <span className="badge badge-info">👁️ Info View</span>}
                      </td>
                      <td>{scan.scannerName  || '—'}</td>
                      <td style={{ fontSize: 12 }}>{scan.scannerPhone || '—'}</td>
                      <td style={{ maxWidth: 200, fontSize: 12 }}>{scan.scannerMessage || '—'}</td>
                      <td>
                        {scan.scannerLocation
                          ? <a href={`https://maps.google.com/?q=${scan.scannerLocation}`}
                              target="_blank" rel="noreferrer"
                              style={{ fontSize: 12, color: '#0066cc' }}>📍 Map</a>
                          : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: '#888' }}>{scan.scannerIp || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          /* Family scans table */
          filteredFamily.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">👨‍👩‍👧</div><p>No family scans found</p></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date/Time</th>
                    <th>Type</th>
                    <th>Finder Name</th>
                    <th>Finder Phone</th>
                    <th>Message</th>
                    <th>Location</th>
                    <th>IP Address</th>
                    <th>Read</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFamily.map((scan, idx) => (
                    <tr key={scan.id}>
                      <td style={{ color: '#888' }}>{idx + 1}</td>
                      <td style={{ fontSize: 12 }}>{fmt(scan.createdAt)}</td>
                      <td>
                        {scan.emergencyAlert
                          ? <span className="badge badge-danger">🚨 Emergency</span>
                          : <span className="badge badge-info">👁️ Info View</span>}
                      </td>
                      <td>{scan.scannerName  || '—'}</td>
                      <td style={{ fontSize: 12 }}>{scan.scannerPhone || '—'}</td>
                      <td style={{ maxWidth: 200, fontSize: 12 }}>{scan.scannerMessage || '—'}</td>
                      <td>
                        {scan.scannerLocation
                          ? <a href={`https://maps.google.com/?q=${scan.scannerLocation}`}
                              target="_blank" rel="noreferrer"
                              style={{ fontSize: 12, color: '#0066cc' }}>📍 Map</a>
                          : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: '#888' }}>{scan.scannerIp || '—'}</td>
                      <td>
                        <span className={`badge ${scan.isRead ? 'badge-success' : 'badge-warning'}`}>
                          {scan.isRead ? 'Read' : 'Unread'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
